<template>
  <div class="login-container">
    <!-- Main Content -->
    <div class="login-wrapper">
      <!-- Left Side - Branding -->
      <div class="branding-side">
        <div class="brand-content">
          <div class="icon-badge">
            <svg viewBox="0 0 24 24" fill="none" class="display-icon">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 21h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M12 17v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </div>
          <h1 class="brand-title">Order Display</h1>
          <p class="brand-subtitle">System</p>
          <div class="feature-list">
            <div class="feature-item">
              <span class="feature-dot"></span>
              <span>Echtzeit-Bestellungen</span>
            </div>
            <div class="feature-item">
              <span class="feature-dot"></span>
              <span>Multi-Station Support</span>
            </div>
            <div class="feature-item">
              <span class="feature-dot"></span>
              <span>Automatische Synchronisation</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Login Form -->
      <div class="form-side">
        <div class="form-container">
          <!-- Logo -->
          <div class="logo-section">
            <img src="/images/MealWaveAsAtext.png" alt="MealWave" class="logo-img" />
          </div>

          <!-- Error Message -->
          <div v-if="error" class="error-alert">
            <svg class="error-icon" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              <path d="M12 8v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="12" cy="16" r="1" fill="currentColor"/>
            </svg>
            <span>{{ error }}</span>
          </div>

          <!-- Login Form -->
          <form @submit.prevent="handleLogin" class="login-form">
            <!-- Email Input -->
            <div class="input-group">
              <label class="input-label">E-Mail Adresse</label>
              <div class="input-wrapper">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M2 7l10 7 10-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <input
                  v-model="email"
                  type="email"
                  class="form-input"
                  placeholder="ods@restaurant.com"
                  required
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Password Input -->
            <div class="input-group">
              <label class="input-label">Passwort</label>
              <div class="input-wrapper">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
                </svg>
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input"
                  placeholder="••••••••••"
                  required
                  :disabled="loading"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="toggle-password"
                  :disabled="loading"
                >
                  <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" stroke="currentColor" stroke-width="1.5"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" stroke="currentColor" stroke-width="1.5"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M3 21L21 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Login Button -->
            <button
              type="submit"
              :disabled="loading || !email || !password"
              class="login-btn"
            >
              <span v-if="loading" class="btn-loading">
                <svg class="spinner" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="60" stroke-dashoffset="20"/>
                </svg>
                <span>Anmeldung läuft...</span>
              </span>
              <span v-else class="btn-content">
                <span>Anmelden</span>
                <svg viewBox="0 0 24 24" fill="none" class="btn-arrow">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          </form>

          <!-- Help Text -->
          <p class="help-text">
            Zugangsdaten erhalten Sie von Ihrem Restaurant-Administrator
          </p>

          <!-- Footer -->
          <div class="footer">
            <span>© 2026 MealWave</span>
            <span class="separator">•</span>
            <span>Alle Rechte vorbehalten</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'LoginView',
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      error: ''
    }
  },
  computed: {
    firebase() {
      return this.$store.state.firebase;
    }
  },
  methods: {
    ...mapActions(['setFirebase']),

    async handleLogin() {
      if (!this.email || !this.password) {
        this.error = 'Bitte füllen Sie alle Felder aus';
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        // Import Firebase functions
        const { signInWithEmailAndPassword } = await import('firebase/auth');
        const { ref, get } = await import('firebase/database');

        if (!this.firebase) {
          this.error = 'Firebase nicht initialisiert';
          this.loading = false;
          return;
        }

        const auth = this.firebase.auth();
        const db = this.firebase.database();

        try {
          // Authenticate with Firebase Auth using email/password
          const userCredential = await signInWithEmailAndPassword(auth, this.email.trim(), this.password);
          const user = userCredential.user;
          console.log('Authentication successful:', user.uid);

          // Get custom claims to find restaurant ID
          const idTokenResult = await user.getIdTokenResult();
          console.log('Custom claims:', idTokenResult.claims);

          const restaurantId = idTokenResult.claims.restaurantId;
          const role = idTokenResult.claims.role;

          // Verify user has ODS role
          if (role !== 'ods') {
            this.error = 'Dieses Konto hat keinen Zugriff auf das Order Display System';
            await auth.signOut();
            this.loading = false;
            return;
          }

          if (!restaurantId) {
            this.error = 'Kein Restaurant mit diesem Konto verknüpft';
            await auth.signOut();
            this.loading = false;
            return;
          }

          console.log('Restaurant ID from claims:', restaurantId);

          // Get the ID token
          const token = await user.getIdToken();

          // Verify restaurant exists in database
          const restaurantRef = ref(db, `restaurants/${restaurantId}`);
          const snapshot = await get(restaurantRef);

          if (!snapshot.exists()) {
            this.error = 'Restaurant nicht gefunden';
            await auth.signOut();
            this.loading = false;
            return;
          }

          const restaurantData = snapshot.val();
          const restaurantInfo = {
            id: restaurantId,
            name: restaurantData.info?.name || restaurantId
          };

          // Store authentication state using store action
          this.$store.dispatch('login', {
            restaurantId: restaurantId,
            user: user,
            token: token
          });

          // Set restaurant in store
          this.$store.commit('SET_SELECTED_RESTAURANT', restaurantInfo);
          localStorage.setItem('restaurantId', restaurantInfo.id);
          localStorage.setItem('restaurantName', restaurantInfo.name);
          localStorage.setItem('userEmail', user.email);
          localStorage.setItem('userId', user.uid);

          // Redirect to station selection
          this.$router.push('/station-selection').catch(() => {});

        } catch (authError) {
          console.error('Firebase Auth error:', authError);

          // Handle specific Firebase errors with German messages
          switch (authError.code) {
            case 'auth/invalid-email':
              this.error = 'Ungültige E-Mail-Adresse';
              break;
            case 'auth/user-not-found':
              this.error = 'Kein Konto mit dieser E-Mail gefunden';
              break;
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
              this.error = 'Ungültige E-Mail oder Passwort';
              break;
            case 'auth/too-many-requests':
              this.error = 'Zu viele Anmeldeversuche. Bitte später erneut versuchen.';
              break;
            case 'auth/network-request-failed':
              this.error = 'Netzwerkfehler. Bitte Internetverbindung prüfen.';
              break;
            default:
              this.error = 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
          }
        }

      } catch (error) {
        console.error('Login error:', error);
        this.error = 'Ein Fehler ist aufgetreten. Bitte erneut versuchen.';
      } finally {
        this.loading = false;
      }
    }
  },

  async mounted() {
    // Check if already authenticated via Firebase
    if (this.firebase?.auth) {
      const { onAuthStateChanged } = await import('firebase/auth');
      const auth = this.firebase.auth();

      // Check current auth state
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          // User is already signed in - verify they have ODS role
          try {
            const idTokenResult = await user.getIdTokenResult();
            if (idTokenResult.claims.role === 'ods' && idTokenResult.claims.restaurantId) {
              // Already logged in with ODS role, redirect
              this.$router.push('/station-selection').catch(() => {});
            }
          } catch (error) {
            console.error('Error checking auth state:', error);
          }
        }
      }, {
        once: true
      });
    }
  }
}
</script>

<style scoped>
.login-container { min-height: 100vh; width: 100%; background: var(--ods-bg); display: flex; align-items: center; justify-content: center; padding: 20px; }
.login-wrapper { display: flex; width: 100%; max-width: 960px; min-height: 560px; background: var(--ods-surface); border-radius: var(--ods-radius-lg); border: 1px solid var(--ods-line); box-shadow: var(--ods-shadow-lg); overflow: hidden; }
.branding-side { flex: 1; background: var(--ods-accent); color: #fff; padding: 56px 48px; display: flex; flex-direction: column; justify-content: center; }
.brand-content { position: relative; }
.icon-badge { width: 64px; height: 64px; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.3); border-radius: var(--ods-radius); display: flex; align-items: center; justify-content: center; margin-bottom: 28px; }
.display-icon { width: 32px; height: 32px; color: #fff; }
.brand-title { font-size: 40px; font-weight: 800; color: #fff; line-height: 1.05; margin: 0; letter-spacing: -.5px; }
.brand-subtitle { font-size: 40px; font-weight: 800; color: rgba(255,255,255,.75); margin: 0 0 36px; letter-spacing: -.5px; }
.feature-list { display: flex; flex-direction: column; gap: 12px; }
.feature-item { display: flex; align-items: center; gap: 12px; color: rgba(255,255,255,.9); font-size: 15px; font-weight: 600; }
.feature-dot { width: 8px; height: 8px; background: #fff; border-radius: 2px; }
.form-side { flex: 1; padding: 56px 48px; display: flex; flex-direction: column; justify-content: center; }
.form-container { width: 100%; max-width: 360px; margin: 0 auto; }
.logo-section { text-align: center; margin-bottom: 32px; }
.logo-img { height: 36px; width: auto; }
html.dark .logo-img { filter: brightness(1.25); }
.error-alert { display: flex; align-items: center; gap: 10px; padding: 12px 14px; background: var(--ods-danger-tint); border: 1px solid var(--ods-danger); border-radius: var(--ods-radius); margin-bottom: 20px; animation: shake .4s ease-in-out; }
@keyframes shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-6px); } 40%, 80% { transform: translateX(6px); } }
.error-icon { width: 20px; height: 20px; color: var(--ods-danger-ink); flex-shrink: 0; }
.error-alert span { color: var(--ods-danger-ink); font-size: 14px; font-weight: 600; }
.login-form { display: flex; flex-direction: column; gap: 18px; }
.input-group { display: flex; flex-direction: column; gap: 6px; }
.input-label { font-size: 11px; font-weight: 800; color: var(--ods-ink-3); text-transform: uppercase; letter-spacing: .07em; }
.input-wrapper { position: relative; display: flex; align-items: center; }
.input-icon { position: absolute; left: 14px; width: 18px; height: 18px; color: var(--ods-ink-3); pointer-events: none; }
.form-input { width: 100%; height: 48px; padding: 0 14px 0 44px; background: var(--ods-surface-2); border: 1px solid var(--ods-line-2); border-radius: var(--ods-radius); color: var(--ods-ink); font-size: 15px; font-family: inherit; font-weight: 600; transition: border-color .15s, box-shadow .15s; }
.form-input::placeholder { color: var(--ods-ink-3); font-weight: 500; }
.form-input:focus { outline: none; border-color: var(--ods-accent); box-shadow: 0 0 0 3px var(--ods-accent-tint); }
.input-wrapper:focus-within .input-icon { color: var(--ods-accent-ink); }
.toggle-password { position: absolute; right: 12px; background: none; border: none; padding: 4px; cursor: pointer; color: var(--ods-ink-3); }
.toggle-password:hover { color: var(--ods-ink); }
.toggle-password svg { width: 20px; height: 20px; }
.login-btn { width: 100%; height: 52px; background: var(--ods-accent); border: none; border-radius: var(--ods-radius); color: #fff; font-size: 16px; font-weight: 800; font-family: inherit; cursor: pointer; transition: background .15s; margin-top: 4px; }
.login-btn:hover:not(:disabled) { background: var(--ods-accent-2); }
.login-btn:active:not(:disabled) { transform: translateY(1px); }
.login-btn:disabled { background: var(--ods-surface-3); color: var(--ods-ink-3); cursor: not-allowed; }
.btn-content, .btn-loading { display: flex; align-items: center; justify-content: center; gap: 10px; }
.btn-arrow { width: 20px; height: 20px; }
.spinner { width: 20px; height: 20px; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.help-text { text-align: center; color: var(--ods-ink-3); font-size: 13px; margin-top: 24px; line-height: 1.5; }
.footer { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 32px; padding-top: 18px; border-top: 1px solid var(--ods-line); font-size: 12px; color: var(--ods-ink-3); }
.separator { color: var(--ods-accent-ink); }
@media (max-width: 900px) { .login-wrapper { flex-direction: column; max-width: 480px; min-height: auto; } .branding-side { padding: 32px 28px; } .brand-title, .brand-subtitle { font-size: 30px; } .feature-list { display: none; } .form-side { padding: 32px 28px; } }
@media (max-width: 480px) { .login-container { padding: 10px; } .branding-side { padding: 24px 20px; } .icon-badge { width: 52px; height: 52px; margin-bottom: 16px; } .brand-title, .brand-subtitle { font-size: 26px; } .form-side { padding: 24px 20px; } }
</style>
